import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { getJson, postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { executionTone, operationName } from "../ai/ai";
import { Counter, DataTable, RowMenu, RowsSkeleton, Window, useIsMobile } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, TextField, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import { type OrgHead } from "./employees";
import { Failed, replyMessage } from "./EmployeesPages";
import { DayGroup, LineNotice } from "./ConversationsPages";
import {
  byDay,
  duration,
  responderText,
  sessionText,
  simChatHref,
  timeIn,
  type SimClientRow,
  type SimMessage,
  type SimSession,
} from "./messaging";
import { MessagingHead } from "./MessagingPages";
import { initials, personHue, plural, whenIn } from "./organizations";
import { ConfirmWindow } from "./PersonActions";
import own from "./messaging.module.css";

// R6 · Empresa › Mensajería › Simulador y la conversación simulada (§5.7). El turno va por Platform (que valida el actor)
// al pipeline de simulación de Messaging y queda en AI Usage/Trace; nunca sale por WhatsApp.

/**
 * `emp-simulador` · «Nuevo cliente simulado» (ventana `cliente-sim`) y la tabla Cliente · Sesiones · Última actividad;
 * clic en un cliente abre su conversación simulada. Un cliente sin contacto todavía no se puede abrir (lo dice su fila).
 * R8 · AR13: la lista se pagina en Messaging de 10 en 10 con su total (página en la dirección; carga al bajar en el
 * celular), nunca entera.
 */
export function SimulatorPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const list = useServerList<SimClientRow>(`/api/admin/organizations/${orgId}/simulation/clients`);
  const [creating, setCreating] = useState(false);
  const organization = (list.extra?.organization as OrgHead | undefined) ?? null;
  useOpenOrg(organization);
  useTitle(`${L("ADM_ORG_MESSAGING")} · ${L("ADM_MSG_TAB_SIMULATOR")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const add = (
    <>
      <button type="button" className={`tm-btn primary ${ui.desktopInline}`} onClick={() => setCreating(true)}>
        {L("SIM_NEW_CLIENT")}
      </button>
      <button
        type="button"
        className={ui.fab}
        aria-label={L("SIM_NEW_CLIENT")}
        title={L("SIM_NEW_CLIENT")}
        onClick={() => setCreating(true)}
      >
        <ChatIcon name="plus" />
      </button>
    </>
  );
  const head = (
    <MessagingHead orgId={orgId} current="simulator" sub={L("ADM_MSG_SIMULATOR_LINE")}>
      {add}
    </MessagingHead>
  );
  const zone = organization?.timezone ?? "America/Lima";
  let body: ReactNode;
  if (list.state !== "ready") {
    if (list.state !== "loading")
      return <Failed head={head} status={list.state === "forbidden" ? 403 : 502} retry={list.reload} />;
    body = (
      <>
        <Counter>
          <span className={ui.bone} style={{ display: "inline-block", width: 110, height: 12 }} />
        </Counter>
        <RowsSkeleton rows={3} table={!mobile} />
      </>
    );
  } else if (list.items.length === 0) {
    body = (
      <div className="tm-empty" role="status" data-part="vacio">
        <strong>{L("ADM_MSG_SIM_EMPTY")}</strong>
        <span>{L("ADM_MSG_SIM_EMPTY_LINE")}</span>
        <button type="button" className="tm-btn primary" onClick={() => setCreating(true)}>
          {L("SIM_NEW_CLIENT")}
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <Counter>{plural(L, locale, list.total, "ADM_MSG_N_SIM_CLIENTS_ONE", "ADM_MSG_N_SIM_CLIENTS")}</Counter>
        <div className={own.fit}>
          <DataTable
            label={L("ADM_MSG_TAB_SIMULATOR")}
            rows={list.items}
            rowKey={(row) => `${row.contactId ?? "x"}-${row.name}`}
            href={(row) => (row.contactId ? simChatHref(orgId, row.contactId) : null)}
            columns={[
              {
                key: "client",
                title: L("ADM_MSG_COL_CLIENT"),
                render: (row) => (
                  <span className={own.withMark}>
                    <span
                      className={`${own.initials} npa-person-tone`}
                      style={{ ["--person-hue" as string]: String(personHue(row.contactId ?? row.name)) }}
                      aria-hidden="true"
                    >
                      {initials(row.name)}
                    </span>
                    <span className={own.twoLines}>
                      <strong title={row.name}>{row.name}</strong>
                      {row.contactId ? (
                        row.testNumber ? (
                          // RC1 · K5 (AF9, C28): con el mismo nombre que otro, la segunda línea dice cuál es.
                          <span data-part="numero-prueba">{formatText(L("ADM_MSG_SIM_TEST_N"), row.testNumber)}</span>
                        ) : null
                      ) : (
                        <span>{L("SIM_NO_CONTACT")}</span>
                      )}
                    </span>
                  </span>
                ),
              },
              {
                key: "sessions",
                title: L("ADM_MSG_COL_SESSIONS"),
                numeric: true,
                render: (row) => row.sessions.toLocaleString(locale),
              },
              {
                key: "last",
                title: L("SIM_LAST_ACTIVITY"),
                render: (row) => whenIn(L, locale, row.lastActivity, zone),
              },
            ]}
            mobile={(row) => ({
              title: row.name,
              line: row.contactId
                ? [
                    row.testNumber ? formatText(L("ADM_MSG_SIM_TEST_N"), row.testNumber) : null,
                    plural(L, locale, row.sessions, "ADM_MSG_N_SESSIONS_ONE", "ADM_MSG_N_SESSIONS"),
                    whenIn(L, locale, row.lastActivity, zone),
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : L("SIM_NO_CONTACT"),
            })}
          />
        </div>
        <ListFooter list={list} />
      </>
    );
  }
  return (
    <>
      {head}
      {body}
      <SimClientWindow
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={async (name) => {
          const reply = await postJson<{
            saved?: boolean;
            contactId?: number | null;
            messageKey?: string;
            field?: string;
          }>(`/api/admin/organizations/${orgId}/simulation/clients`, { name });
          if (reply.status !== 200 || !reply.body?.saved) return replyMessage(L, reply.body);
          toast.show(L("ADM_MSG_SIM_CREATED"));
          // El cliente nuevo se abre en su conversación simulada; sin contacto, queda en la lista.
          if (reply.body.contactId) navigate(simChatHref(orgId, reply.body.contactId));
          else list.reload();
          return null;
        }}
      />
    </>
  );
}

/** Ventana `cliente-sim` · «Nuevo cliente simulado» con el nombre (lo escrito se conserva ante un error). */
function SimClientWindow({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<string | null>;
}) {
  const { L } = useTexts();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setName("");
      setError(null);
    }
  }
  const create = async () => {
    if (!name.trim()) {
      setError(L("SIM_NEED_NAME"));
      requestAnimationFrame(() => document.getElementById("sim-name")?.focus());
      return;
    }
    setBusy(true);
    const failure = await onCreate(name.trim());
    setBusy(false);
    if (failure) setError(failure);
    else onClose();
  };
  return (
    <Window
      open={open}
      title={L("SIM_NEW_CLIENT")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void create()}
          >
            {L("ADM_KB_CREATE")}
          </button>
        </>
      }
    >
      <TextField
        id="sim-name"
        label={L("ADM_AI_NAME")}
        value={name}
        error={error}
        help={L("ADM_MSG_SIM_NAME_HELP")}
        maxLength={150}
        placeholder={L("SIM_NAME_PLACEHOLDER")}
        onChange={(value) => {
          setName(value);
          if (value.trim()) setError(null);
        }}
      />
    </Window>
  );
}

type SimRecord = {
  organization: OrgHead;
  client: {
    contactId: number;
    name: string;
    sessions: { id: number; number: number; open: boolean; messages: number; lastActivity: string | null }[];
  };
  session: SimSession | null;
  executions: { id: number; operation: string; status: string; latencyMs: number | null; at: string }[] | null;
};

/**
 * `emp-sim-chat` · ← al Simulador, el cliente con «Simulación» y su sesión, «Atiende: …». Aviso fijo «Simulación: no se
 * envía nada por WhatsApp.», la conversación (C19) con la caja para escribir como el cliente y el panel de detalle (C23;
 * pantalla completa en el celular) con los datos del cliente simulado, sus sesiones y las trazas de la sesión. «Cerrar e
 * iniciar otra» confirma (C12): la sesión nueva empieza con el próximo mensaje (el servicio la abre al recibirlo).
 */
export function SimChatPage({
  orgId,
  contactId,
  sessionId,
}: {
  orgId: number;
  contactId: number;
  sessionId: number | null;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const data = useAiData<SimRecord>(
    `/api/admin/organizations/${orgId}/simulation/clients/${contactId}${sessionId ? `?sessionId=${sessionId}` : ""}`,
  );
  // Panel abierto por defecto cuando cabe junto a la conversación (C23); más angosto, se abre superpuesto a pedido.
  const [panelOpen, setPanelOpen] = useState(window.matchMedia("(min-width: 1200px)").matches);
  const [confirming, setConfirming] = useState(false);
  // «Cerrar e iniciar otra» confirmado: el próximo mensaje cierra la sesión actual y abre la siguiente.
  const [fresh, setFresh] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [aiFailed, setAiFailed] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [earlier, setEarlier] = useState<{ messages: SimMessage[]; hasMore: boolean } | null>(null);
  const transcript = useRef<HTMLDivElement>(null);
  useOpenOrg(data.data?.organization ?? null);
  const record = data.data;
  useTitle(record?.client.name ?? L("ADM_MSG_TAB_SIMULATOR"));
  const session = fresh ? null : (record?.session ?? null);
  const messageCount = session?.messages.length ?? 0;
  // Lo último de la conversación a la vista al abrirla y al llegar un mensaje (sin mover la página entera).
  useEffect(() => {
    const element = transcript.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messageCount, session?.id]);
  const back = { href: `/Organizations/Simulator?id=${orgId}`, label: L("ADM_MSG_TAB_SIMULATOR") };
  if (data.state === "missing")
    return (
      <PageState
        head={<PageHead title={L("ADM_MSG_TAB_SIMULATOR")} back={back} />}
        title={L("ADM_MSG_SIM_MISSING")}
        line={L("ADM_MSG_SIM_MISSING_LINE")}
        action={
          <a className="tm-btn secondary" href={back.href}>
            {L("ADM_MSG_BACK_SIMULATOR")}
          </a>
        }
      />
    );
  if (!record) {
    const head = <PageHead title={L("ADM_MSG_TAB_SIMULATOR")} back={back} />;
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        <PageHead
          title=""
          back={back}
          sub={<span className={ui.bone} style={{ display: "inline-block", width: 180, height: 12 }} />}
        />
        <LineNotice icon="info">{L("ADM_MSG_SIM_NOTICE")}</LineNotice>
        <div className={own.chat} aria-busy="true">
          <RowsSkeleton rows={3} />
        </div>
      </>
    );
  }
  const zone = record.organization.timezone;
  const client = record.client;
  const openOther = client.sessions.find((item) => item.open && item.id !== session?.id) ?? null;
  const messages = [...(earlier?.messages ?? []), ...(session?.messages ?? [])];
  const hasMore = earlier ? earlier.hasMore : (session?.hasMore ?? false);
  const canWrite = !session || session.open;
  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    setSendError(null);
    const reply = await postJson<{
      saved?: boolean;
      sessionId?: number;
      sessionNumber?: number;
      aiFailed?: boolean;
      messageKey?: string;
    }>(`/api/admin/organizations/${orgId}/simulation/turns`, {
      contactId,
      sessionId: fresh ? (record.session?.open ? record.session.id : null) : (session?.id ?? null),
      content,
      forceNew: fresh && !!record.session?.open,
    });
    setSending(false);
    if (reply.status !== 200 || !reply.body?.saved || !reply.body.sessionId) {
      setSendError(replyMessage(L, reply.body));
      // El mensaje pudo quedar guardado aunque no hubo respuesta: se vuelve a leer antes de reenviar.
      if (reply.status >= 500) void data.reload();
      return;
    }
    setText("");
    setAiFailed(!!reply.body.aiFailed);
    const started = reply.body.sessionId !== session?.id;
    setFresh(false);
    setEarlier(null);
    if (started) {
      if (session || fresh) toast.show(formatText(L("ADM_MSG_SESSION_STARTED"), reply.body.sessionNumber ?? ""));
      navigate(simChatHref(orgId, contactId, reply.body.sessionId), { replace: true });
    } else void data.reload();
  };
  const onKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };
  const loadEarlier = async () => {
    const first = messages[0];
    if (!session || !first) return;
    const reply = await getJson<SimSession>(
      `/api/admin/organizations/${orgId}/simulation/sessions/${session.id}?before=${first.id}`,
    );
    if (reply.status === 200 && reply.body)
      setEarlier({ messages: [...reply.body.messages, ...(earlier?.messages ?? [])], hasMore: reply.body.hasMore });
    else toast.show(L("ADM_UNAVAILABLE_LINE"));
  };
  const menu = [
    ...(session?.open ? [{ label: L("ADM_MSG_CLOSE_START_MENU"), onSelect: () => setConfirming(true) }] : []),
    ...(mobile || !panelOpen ? [{ label: L("ADM_MSG_SEE_DETAIL"), onSelect: () => setPanelOpen(true) }] : []),
  ];
  const sub = session
    ? formatText(L("ADM_MSG_ATTENDS"), responderText(L, session.mode), session.inbox ?? "—")
    : fresh
      ? L("ADM_MSG_SESSION_NEW")
      : undefined;
  const author = (message: SimMessage) =>
    message.from === "ai" ? L("SIM_MODE_AI") : message.from === "person" ? L("ADM_MSG_RESPONDER_PERSON") : null;
  const panel = panelOpen ? (
    <aside className={own.panel} aria-labelledby="sim-detail-title" data-part="panel">
      <div className={own.panelHead}>
        {mobile ? (
          <button
            type="button"
            className={own.iconButton}
            aria-label={L("ADM_MSG_CLOSE_DETAIL")}
            onClick={() => setPanelOpen(false)}
          >
            <ChatIcon name="arrow-left" />
          </button>
        ) : null}
        <h2 id="sim-detail-title">{L("ADM_MSG_DETAIL")}</h2>
        {mobile ? null : (
          <button
            type="button"
            className={own.iconButton}
            aria-label={L("ADM_MSG_CLOSE_DETAIL")}
            title={L("ADM_MSG_CLOSE_DETAIL")}
            onClick={() => setPanelOpen(false)}
          >
            <ChatIcon name="x" />
          </button>
        )}
      </div>
      <div className={own.panelBody}>
        <div className={own.panelTitle}>
          <strong>{client.name}</strong>
          <span className="tm-tag">{L("CH_SIM")}</span>
        </div>
        <dl className={own.facts}>
          <div>
            <dt>{L("SIM_CHANNEL")}</dt>
            <dd>{L("ADM_MSG_SIM_CHANNEL")}</dd>
          </div>
          <div>
            <dt>{L("SIM_INBOX")}</dt>
            <dd>{session?.inbox ?? "—"}</dd>
          </div>
          <div>
            <dt>{L("SIM_SESSION")}</dt>
            <dd>
              {session
                ? formatText(L(session.open ? "ADM_MSG_N_OPEN_SHORT" : "ADM_MSG_N_CLOSED_SHORT"), session.number)
                : "—"}
            </dd>
          </div>
          <div>
            <dt>{L("ADM_MSG_RESPONDS")}</dt>
            <dd>{session ? responderText(L, session.mode) : "—"}</dd>
          </div>
        </dl>
        <section className={own.panelSection} aria-labelledby="sim-sessions">
          <h3 id="sim-sessions">{L("ADM_MSG_SESSIONS")}</h3>
          {client.sessions.length === 0 ? (
            <p className={own.panelNote}>{L("SIM_NO_SESSIONS")}</p>
          ) : (
            <ul className={own.panelList}>
              {client.sessions.map((item) => (
                <li key={item.id}>
                  <a
                    className={own.panelItem}
                    href={simChatHref(orgId, contactId, item.id)}
                    aria-current={item.id === session?.id ? "page" : undefined}
                  >
                    {sessionText(L, item.number, item.open)}
                    <span>
                      {plural(L, locale, item.messages, "ADM_MSG_N_MESSAGES_ONE", "ADM_MSG_N_MESSAGES")} ·{" "}
                      {whenIn(L, locale, item.lastActivity, zone)}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className={own.panelSection} aria-labelledby="sim-traces">
          <h3 id="sim-traces">{L("ADM_MSG_TRACES")}</h3>
          {!session ? (
            <p className={own.panelNote}>{L("ADM_MSG_TRACES_NONE")}</p>
          ) : record.executions == null ? (
            <p className={own.panelNote}>{L("ADM_UNAVAILABLE_LINE")}</p>
          ) : record.executions.length === 0 ? (
            <p className={own.panelNote}>{L("ADM_MSG_TRACES_EMPTY")}</p>
          ) : (
            <ul className={own.panelList}>
              {record.executions.map((item) => (
                <li key={item.id}>
                  <a className={own.panelItem} href={`/Ai/Uso/Detail/${item.id}`}>
                    {formatText(L("ADM_MSG_EXECUTION"), item.id)}
                    <span>
                      {[
                        operationName(L, item.operation),
                        executionTone(L, item.status).text.toLowerCase(),
                        duration(locale, item.latencyMs),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </aside>
  ) : null;
  return (
    <div className={`${own.simLayout}${panelOpen && !mobile ? "" : ` ${own.closed}`}`}>
      <div className={`${own.simMain} ${own.wrapHead}`}>
        <PageHead
          title={client.name}
          back={back}
          titleTag={
            <>
              <span className="tm-tag">{L("CH_SIM")}</span>
              {session ? <span className="tm-tag">{sessionText(L, session.number, session.open)}</span> : null}
            </>
          }
          sub={sub}
        >
          <span className={own.headActions}>
            {!mobile && !panelOpen ? (
              <button
                type="button"
                className={own.iconButton}
                aria-label={L("ADM_MSG_SEE_DETAIL")}
                title={L("ADM_MSG_SEE_DETAIL")}
                onClick={() => setPanelOpen(true)}
              >
                <ChatIcon name="panel-right" />
              </button>
            ) : null}
            {menu.length ? <RowMenu label={L("ADM_MORE_ACTIONS")} title={client.name} items={menu} /> : null}
          </span>
        </PageHead>
        <LineNotice icon="info">{L("ADM_MSG_SIM_NOTICE")}</LineNotice>
        {aiFailed ? <Notice tone="warning">{L("ADM_MSG_AI_FAILED")}</Notice> : null}
        <div className={own.chat}>
          <div ref={transcript} className={own.transcript} role="log" aria-label={L("SIM_CONVERSATION")} tabIndex={0}>
            {hasMore ? (
              <button type="button" className={own.earlier} onClick={() => void loadEarlier()}>
                {L("ADM_MSG_EARLIER")}
              </button>
            ) : null}
            {messages.length === 0 ? (
              <div className={own.chatEmpty}>
                <strong>{L(fresh ? "ADM_MSG_SESSION_NEW" : "SIM_READY")}</strong>
                <span>{L("SIM_START_HINT")}</span>
              </div>
            ) : (
              byDay(L, locale, messages, zone).map((group) => (
                <DayGroup key={`${group.day}-${group.items[0]!.id}`} day={group.day}>
                  {group.items.map((message) =>
                    message.from === "system" ? (
                      <p key={message.id} className={own.system}>
                        {message.text}
                      </p>
                    ) : (
                      <div
                        key={message.id}
                        className={`${own.bubble}${message.from === "customer" ? ` ${own.mine}` : ""}`}
                      >
                        {author(message) ? (
                          <span className={`${own.author}${message.from === "ai" ? ` ${own.authorAi}` : ""}`}>
                            {author(message)}
                          </span>
                        ) : null}
                        <span className={own.text}>{message.text ?? "—"}</span>
                        <span className={own.time}>{timeIn(locale, message.at, zone)}</span>
                      </div>
                    ),
                  )}
                </DayGroup>
              ))
            )}
          </div>
          {canWrite ? (
            <div className={own.composer}>
              <label className={own.composerField}>
                <span className="tm-sr">{L("ADM_MSG_WRITE_AS_CLIENT")}</span>
                <textarea
                  rows={1}
                  value={text}
                  placeholder={L("ADM_MSG_WRITE_AS_CLIENT")}
                  maxLength={4000}
                  aria-invalid={sendError ? "true" : undefined}
                  aria-describedby={sendError ? "sim-send-error" : undefined}
                  onChange={(event) => {
                    setText(event.target.value);
                    if (sendError) setSendError(null);
                  }}
                  onKeyDown={onKey}
                />
              </label>
              <button
                type="button"
                className={own.send}
                aria-label={L("SIM_SEND")}
                title={L("SIM_SEND")}
                disabled={sending || !text.trim()}
                aria-busy={sending || undefined}
                onClick={() => void send()}
              >
                <ChatIcon name="send" />
              </button>
            </div>
          ) : (
            <div className={own.readonly}>
              <span>
                <ChatIcon name="lock" /> {L("SIM_READONLY_NOTICE")}
              </span>
              {openOther ? (
                <a className="tm-btn secondary" href={simChatHref(orgId, contactId, openOther.id)}>
                  {L("ADM_MSG_SEE_OPEN_SESSION")}
                </a>
              ) : (
                <button type="button" className="tm-btn secondary" onClick={() => (setFresh(true), setEarlier(null))}>
                  {L("SIM_START_SESSION")}
                </button>
              )}
            </div>
          )}
        </div>
        {sendError ? (
          <p id="sim-send-error" className="tm-field-error" role="alert">
            {sendError}
          </p>
        ) : null}
      </div>
      {panel}
      <ConfirmWindow
        open={confirming}
        danger={false}
        title={formatText(L("ADM_MSG_CLOSE_START_TITLE"), session?.number ?? "")}
        line={L("ADM_MSG_CLOSE_START_LINE")}
        action={L("ADM_MSG_CLOSE_START")}
        onClose={() => setConfirming(false)}
        onConfirm={async () => {
          setFresh(true);
          setEarlier(null);
          setAiFailed(false);
          toast.show(L("ADM_MSG_SESSION_NEXT"));
          return true;
        }}
      />
    </div>
  );
}
